import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Fade } from '@material-ui/core';
import { CardapioHeader } from './cardapio-header';
import { CardapioHeaderNavBar } from './cardapio-navbar';
import {
  Sessoes,
  Sessao,
} from 'modulos/cardapio/componentes';
import { useHistory, useParams } from 'react-router-dom';
import { useCardapioApi, useMasterApi, useCardapioLogica, EnumTipoScanner } from './hooks';
import { CardapioProps } from './cardapio-props';
import { useStyles } from './cardapio-styles';
import { SessoesInstancia } from 'modulos/cardapio/componentes/produtos';
import { CardapioFooter } from './cardapio-footer';
import { EditarDados } from 'modulos/cardapio/paginas/edicao/edicao-header/editar-dados';
import { ButtonSacola } from './cardapio-sacola/componentes';
import { isValidUUIDV4 } from 'utils/is-valid-id';
import { TemaModel } from 'api/cardapio/models/tema/tema-model';
import { DialogScannerComanda } from './cardapio-scanner-comanda';
import { EmpresaModel } from 'api/cardapio/models/empresa/empresa-model';
import { CardapioDadosProps } from './cardapio-header/cardapio-header-props';
import { HorarioModel } from 'api/cardapio/models/horario/horario-model';
import classNames from 'classnames';
import { useMesaStore } from 'services/store/mesa-store';
import { ConfigDeliveryModel } from 'api/cardapio/models/delivery/config-delivery-model';
import { CardapioSacola } from './cardapio-sacola';
import { ButtonAcompanharPedido } from './cardapio-sacola/componentes/button-acompanhar-pedido';
import { usePedidos } from 'services/providers/pedidos-provider';
import { useMovProd } from 'services/hooks/mov-prod';
import { useLoginEvent } from 'services/hooks/login-event';
import { Carregando, QRCodeModal } from 'components';
import { DialogAdicionarProdutoComSubItem } from 'components/dialogs/dialog-adicionar-produto-com-subitem/dialog-adicionar-produto-com-subitem';
import { MasterPagina } from 'views/pages/templates';
import { VisualizarProdutoModal } from 'components/dialogs/visualizar-produto-modal';
import { useCardapioDadosStore } from 'services/store/cardapio-dados-store';
import { AcompanharPedido } from 'views/pages/acompanhar-pedido/acompanhar-pedido-page';
import { EnumModals, useModalStore } from 'services/store/modal-store';
import { getIDFromUrl } from 'utils/get-id-url';
import { useObterToken } from 'services/store/obter-token';

export const Cardapio = ({ visualizacao }: CardapioProps) => {

  // DEPENDENCIAS
  const classes = useStyles();

  // STATES, REFS E PARAMS
  const sessoes = React.useRef<SessoesInstancia>(null);
  const pesquisa = React.useRef<HTMLDivElement | null>(null);
  const menuNavRef = React.useRef<HTMLDivElement | null>(null);
  const { empresaId } = useParams<{ empresaId: string }>()
  const { adicionarEmpresa } = useParams<{ adicionarEmpresa: string }>();
  const [values, setValues] = useState<TemaModel>(new TemaModel());
  const [redefinir, setRedefinir] = useState<boolean>(false);
  const { setCodComanda } = usePedidos();
  const { adicionarProdutoComSubItem, editarProdutoComSubItem } = useMovProd()
  const { location } = useHistory();
  const openCloseModal = useModalStore(state => state.openCloseModal);
  const { setToken } = useObterToken()
  useLoginEvent();

  // ID MESA NA URL
  const getMesaId = useMesaStore(state => state.getMesaId)
  const mesaId = getMesaId(location.pathname);

  // REQUISIÇÕES
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dados: apiDados, operacoes: api } = useCardapioApi(empresaId);
  const { dados: apiMasterDados } = useMasterApi(empresaId);
  const setCardapioDados = useCardapioDadosStore(state => state.setCardapioDados);

  const cardapioDados: CardapioDadosProps = useMemo(() => ({
    empresa: apiMasterDados.data?.empresa ?? new EmpresaModel(),
    configuracoes: apiMasterDados.data?.configuracoes ?? new TemaModel(),
    horarioFuncionamento: apiMasterDados.data?.horarioFuncionamento ?? new HorarioModel(),
    configuracoesEmpresa: apiMasterDados.data?.configuracoesEmpresa ?? [],
    finalizadoras: apiMasterDados.data?.finalizadoras ?? [],
    configuracoesDelivery: apiMasterDados.data?.configuracoesDelivery ?? new ConfigDeliveryModel()
  }), [apiMasterDados.data?.configuracoes, apiMasterDados.data?.configuracoesDelivery, apiMasterDados.data?.configuracoesEmpresa, apiMasterDados.data?.empresa, apiMasterDados.data?.finalizadoras, apiMasterDados.data?.horarioFuncionamento])

  useEffect(() => {
    setCardapioDados(cardapioDados);
  }, [cardapioDados, setCardapioDados])

  const { dados: logicaDados, operacoes: logica } = useCardapioLogica({
    sessoes: sessoes,
    pesquisa: pesquisa,
    menuNavRef: menuNavRef,
    produtos: apiDados.sessoes.data || [],
    empresaId: empresaId,
    cardapioDados: cardapioDados
  });

  const verificarEmpresa = useCallback(() => {

    const produtosImg = apiDados.sessoes.data.map(x => x.produtos).reduce((arr, obj) => arr.concat(obj.map((y: any) => ({
      produtoId: y.produtoId,
      imagemUrl: y.imagemUrl
    }))), [])

    const imgsJson = JSON.stringify(produtosImg)

    localStorage.setItem('ProdutoImgs', imgsJson)
  }, [apiDados.sessoes.data])

  const verificarMesa = useCallback(() => {
    const mesaId = getIDFromUrl('mesaId');
    let idValido: boolean = isValidUUIDV4(mesaId);
    if (idValido) {
      return setToken(undefined);
    }
  }, [setToken]);

  useEffect(() => {
    verificarEmpresa();
    verificarMesa();
  }, [verificarEmpresa, verificarMesa])

  const carregandoCategoria =
    apiMasterDados.isLoading || apiDados.sessoes.isLoading || apiDados.sessoes.isFetching;

  const abrirPedidoResumo = () => {
    if (cardapioDados.configuracoes.tipoUsoCardapio === 1) {
      logica.abrirScannerComanda()
      logica.definirTipoScanner(EnumTipoScanner.VisualizarPedido)
      return
    }
    logica.abrirGarcom()
  }

  const handleCodigo = async (codigo: string) => {
    const prefixo = cardapioDados.configuracoesEmpresa.find(x => x.codigo === 804)
    if (prefixo && prefixo.valor && codigo.startsWith(prefixo.valor)) {
      codigo = codigo.slice(prefixo.valor.length)
    }
    debugger
    if (logicaDados.tipoScanner === EnumTipoScanner.VisualizarPedido) {
      openCloseModal(EnumModals.ResumoPedido, true, {
        codigoComanda: codigo
      })
      return
    }
    setCodComanda(codigo)

    if (cardapioDados.configuracoes.disponibilizarPagamentoFinalizacao) {
      logica.abrirModalFormaPag()
      return;
    }
  }

  if (carregandoCategoria) {
    return (
      <Fade in={true}>
        <div>
          <Carregando
            titulo={
              !apiMasterDados.isLoading
                && (apiDados.sessoes.isLoading || apiDados.sessoes.isFetching)
                ? 'Carregando os produtos'
                : "Preparando Catálogo"}
            mensagem={"Aguarde enquanto organizamos as informações!"}
            modelo="fixed"
            carregando
          />
        </div>
      </Fade>
    )
  }
  return (
    <MasterPagina>
      <React.Fragment>
        <CardapioHeaderNavBar
          cardapioDados={cardapioDados!}
          logout={logica.logout}
          categorias={apiDados.sessoes.data}
          referencia={menuNavRef}
          abrirPedidoResumo={abrirPedidoResumo}
          setConfigurando={logica.configurandoWrapper}
          categoriaSelecionada={logicaDados.categoriaSendoExibida}
          quandoPesquisar={logica.quandoTermoForDigitadoNoNavbar}
          categoriaClick={logica.categoriaClick!}
          visualizacao={visualizacao}
        />
        {(visualizacao === 'cardapio' || visualizacao === 'configuracao') && (
          <div ref={logicaDados.header}>
            <CardapioHeader
              atualizando={false}
              cardapioDados={cardapioDados!}
              className={classes.header}
              mesaId={mesaId}
              setValueProps={setValues}
              redefinir={redefinir}
              visualizacao={visualizacao}
            />
            <Container className={classes.containerSessaoPesquisa} fixed>
              {logicaDados.pesquisando && (
                <div ref={pesquisa}>
                  <Sessao
                    sessaoDeBusca
                    carregando={logicaDados.carregandoPesquisa}
                    destaque={`"${logicaDados.termoPesquisa}"`}
                    className={classes.pesquisa}
                    sessao={{
                      nome: 'Buscando por: ',
                      produtos: logicaDados.resultadoPesquisa.filter(x => x.tipo === 0),
                      ativo: true,
                      breadCrumbs: '',
                      categoriaPaiId: '',
                      contratoId: '',
                      cor: '',
                      descricao: '',
                      id: '',
                      ordem: 0,
                    }}
                    onProdutoClick={logica.abrirEdicaoProduto}
                    adicionarProdutoClick={logica.abrirAdicaoProduto}
                  />
                </div>
              )}
            </Container>
          </div>
        )}
        {visualizacao === 'acompanharPedido' && (
          <AcompanharPedido />
        )}
        {visualizacao === 'gerarQrCode' && (
          <Container className={classNames(classes.container, classes.containerQRCode)} fixed>
            <QRCodeModal
              empresaId={empresaId}
              nomeEmpresa={cardapioDados!.empresa.nomeFantasia}
              infoEmpresa={cardapioDados!.configuracoes.informacaoAdicional}
              tema={cardapioDados.configuracoes!}
              empresaMaster={cardapioDados!.empresa}
            />
          </Container>
        )}
        {visualizacao === 'cardapio' && (
          <>
            <Sessoes
              ref={sessoes}
              carregando={apiDados.sessoes.isLoading || apiDados.sessoes.isFetching}
              sessoes={apiDados.sessoes.data}
              className={classes.sessao}
              quandoSessaoForVisualizada={logica.quandoSessaoForExibida}
              adicionarProdutoClick={logica.abrirAdicaoProduto}
              onProdutoClick={logica.abrirEdicaoProduto}
            />
            <CardapioSacola
              cardapioDados={cardapioDados}
              abrirModalCliente={logica.abrirModalCliente}
              mesaId={mesaId}
            />
            <ButtonAcompanharPedido empresaId={empresaId!} />
            <ButtonSacola
              empresaId={empresaId!}
              colorBadge="secondary"
              size="35"
              produtoEntregaId={cardapioDados?.configuracoesDelivery.produtoEntrega?.id ?? ''}
            />
          </>

        )}
        {visualizacao === 'configuracao' && (
          <EditarDados
            empresaId={empresaId}
            quandoForFechado={logica.logar}
            adicionarEmpresa={adicionarEmpresa}
            logout={logica.logout}
            setValues={setValues}
            values={values}
            redefinir={redefinir}
            setRedefinir={setRedefinir}
          />
        )}
        <CardapioFooter
          capa={cardapioDados?.configuracoes?.capa ?? ''}
          logo={cardapioDados?.configuracoes?.logo ?? ''}
          nomeFantasia={cardapioDados?.empresa?.nomeFantasia ?? ''}
        />
        <VisualizarProdutoModal
          aberto={logicaDados.produtoAberto}
          mesaId={mesaId}
          quandoForFechado={logica.fecharEdicaoProduto}
          produto={logicaDados.produto}
          permiteEntrega={cardapioDados?.configuracoes.permiteEntrega}
          permiteRetirada={cardapioDados?.configuracoes.permiteRetirada}
          fone={cardapioDados?.empresa.fone}
        ></VisualizarProdutoModal>
        {logicaDados.scannerComanda && (
          <DialogScannerComanda
            openned={logicaDados.scannerComanda}
            statusPedido={false}
            closeModal={logica.fecharScannerComanda}
            cardapioDados={cardapioDados}
            empresaId={empresaId}
            mesaId={mesaId}
            handleCodigo={handleCodigo}
          />
        )}
        {logicaDados.dialogSubitem && (
          <DialogAdicionarProdutoComSubItem
            aberto={logicaDados.dialogSubitem}
            adicionarProdutoComSubItem={adicionarProdutoComSubItem}
            editarProdutoComSubItem={editarProdutoComSubItem}
            fechar={() => logica.setDialogSubItem(false)}
            produto={logicaDados.produto}
            mesaId={mesaId}
          />
        )}
      </React.Fragment >
    </MasterPagina >
  );
};